import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';
import { config } from '../config';
import { useAppContext } from '../libs/appContextLib';
import { useFetchUserPreferences } from './useFetchUserPreferences';

/**
 * Hook for managing user-specific route preferences (pins, recents, navigation settings).
 *
 * Fetches data from GET /configure/navigation/user-preferences
 * Mutations update via POST /configure/navigation/user-preferences
 * Uses optimistic updates for instant UI feedback.
 *
 * Usage:
 * const { pinnedRoutes, recentRoutes, navigationPreferences, pinRoute, unpinRoute, trackRecentRoute } = useUserRoutePreferences();
 */
export const useUserRoutePreferences = () => {
  const { currUser } = useAppContext();
  const queryClient = useQueryClient();
  const preferencesUserKey = currUser?.id || currUser?.userId;
  const userPreferencesQueryKey = ['user-preferences', preferencesUserKey];
  const pinnedRoutesStorageKey = currUser?.customerId
    ? `${currUser.customerId}_navigationPinnedRoutes`
    : null;
  const [localPinnedRoutes, setLocalPinnedRoutes] = useState([]);

  const normalizePinnedRoutes = useCallback((pins) => {
    if (!Array.isArray(pins)) return [];
    return pins
      .filter((pinId) => typeof pinId === 'string')
      .slice(0, 10);
  }, []);

  const persistPinnedRoutesLocally = useCallback((pins) => {
    const normalizedPins = normalizePinnedRoutes(pins);
    setLocalPinnedRoutes(normalizedPins);

    if (!pinnedRoutesStorageKey || typeof window === 'undefined') return;

    try {
      window.localStorage.setItem(pinnedRoutesStorageKey, JSON.stringify(normalizedPins));
    } catch (error) {
      console.warn('Failed to persist pinned routes to localStorage:', error);
    }
  }, [normalizePinnedRoutes, pinnedRoutesStorageKey]);

  // Fetch user preferences from dedicated endpoint
  const { data: userPreferences, isLoading } = useFetchUserPreferences();

  // True only after query has returned data for this user.
  // This prevents components from treating fallback defaults as "loaded".
  const hasLoadedPreferences = userPreferences !== undefined;

  // Hydrate pinned routes from localStorage so nav can render pins before network completes.
  useEffect(() => {
    if (!pinnedRoutesStorageKey || typeof window === 'undefined') {
      setLocalPinnedRoutes([]);
      return;
    }

    try {
      const stored = window.localStorage.getItem(pinnedRoutesStorageKey);
      if (!stored) {
        setLocalPinnedRoutes([]);
        return;
      }

      const parsed = JSON.parse(stored);
      setLocalPinnedRoutes(normalizePinnedRoutes(parsed));
    } catch (error) {
      console.warn('Failed to hydrate pinned routes from localStorage:', error);
      setLocalPinnedRoutes([]);
    }
  }, [normalizePinnedRoutes, pinnedRoutesStorageKey]);

  // Keep localStorage in sync with backend truth once preferences have loaded.
  useEffect(() => {
    if (!hasLoadedPreferences) return;
    persistPinnedRoutesLocally(userPreferences?.pinnedRoutes || []);
  }, [hasLoadedPreferences, persistPinnedRoutesLocally, userPreferences?.pinnedRoutes]);

  // Memoize arrays/objects by VALUE, not reference
  // This prevents cascade of re-renders when object changes
  const pinnedRoutes = useMemo(() => {
    if (hasLoadedPreferences) {
      return normalizePinnedRoutes(userPreferences?.pinnedRoutes);
    }
    return localPinnedRoutes;
  }, [hasLoadedPreferences, localPinnedRoutes, normalizePinnedRoutes, userPreferences?.pinnedRoutes]);

  const recentRoutes = useMemo(() => {
    return userPreferences?.recentRoutes || [];
  }, [JSON.stringify(userPreferences?.recentRoutes)]);

  const navigationPreferences = useMemo(() => {
    return userPreferences?.navigationPreferences || {};
  }, [JSON.stringify(userPreferences?.navigationPreferences)]);

  /**
   * Pin a route for quick access
   * Max 10 pins to prevent clutter
   */
  const pinRoute = useMutation({
    mutationFn: async (routeId) => {
      if (pinnedRoutes.includes(routeId)) {
        throw new Error('Route already pinned');
      }

      const updatedPins = [...pinnedRoutes, routeId].slice(0, 10);

      const response = await apiClient.post(
        `${config.REACT_APP_BACKEND_HOST}/configure/navigation/user-preferences`,
        {
          pinnedRoutes: updatedPins,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(userPreferencesQueryKey);
    },
    onError: (error) => {
      console.error('Failed to pin route:', error);
    },
  });

  /**
   * Unpin a route
   */
  const unpinRoute = useMutation({
    mutationFn: async (routeId) => {
      const updatedPins = pinnedRoutes.filter(r => r !== routeId);

      const response = await apiClient.post(
        `${config.REACT_APP_BACKEND_HOST}/configure/navigation/user-preferences`,
        {
          pinnedRoutes: updatedPins,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(userPreferencesQueryKey);
    },
    onError: (error) => {
      console.error('Failed to unpin route:', error);
    },
  });

  /**
   * Toggle pin state (pin if unpinned, unpin if pinned)
   * Uses optimistic update for instant UI feedback
   */
  const togglePin = useMutation({
    mutationFn: async (routeId) => {
      console.log('[togglePin] Mutation started for:', routeId);
      const currentPreferences = queryClient.getQueryData(userPreferencesQueryKey);
      const currentPins = normalizePinnedRoutes(currentPreferences?.pinnedRoutes || pinnedRoutes);
      console.log('[togglePin] Current pinnedRoutes:', currentPins);

      const isPinned = currentPins.includes(routeId);
      const updatedPins = isPinned
        ? currentPins.filter(r => r !== routeId)
        : [...currentPins, routeId].slice(0, 10);

      console.log('[togglePin] isPinned:', isPinned);
      console.log('[togglePin] updatedPins:', updatedPins);

      const response = await apiClient.post(
        `${config.REACT_APP_BACKEND_HOST}/configure/navigation/user-preferences`,
        {
          pinnedRoutes: updatedPins,
        }
      );

      console.log('[togglePin] API response:', response.data);
      return response.data;
    },
    // Optimistic update - UI feels instant, syncs in background
    onMutate: async (routeId) => {
      console.log('[togglePin] onMutate triggered for:', routeId);

      // Cancel any outgoing refetches to prevent overwriting our optimistic update
      await queryClient.cancelQueries(userPreferencesQueryKey);

      // Snapshot the previous value
      const previousPreferences = queryClient.getQueryData(userPreferencesQueryKey);
      const previousLocalPins = localPinnedRoutes;
      console.log('[togglePin] Previous preferences:', previousPreferences);

      const currentPins = normalizePinnedRoutes(previousPreferences?.pinnedRoutes || pinnedRoutes);
      const isPinned = currentPins.includes(routeId);
      const updatedPins = isPinned
        ? currentPins.filter(r => r !== routeId)
        : [...currentPins, routeId].slice(0, 10);

      // Optimistically update to the new value
      queryClient.setQueryData(userPreferencesQueryKey, (old) => {
        if (!old) return old;

        return {
          ...old,
          pinnedRoutes: updatedPins
        };
      });

      persistPinnedRoutesLocally(updatedPins);

      return { previousPreferences, previousLocalPins };
    },
    // If mutation fails, roll back to previous value
    onError: (err, routeId, context) => {
      if (context?.previousPreferences) {
        queryClient.setQueryData(userPreferencesQueryKey, context.previousPreferences);
        persistPinnedRoutesLocally(context.previousPreferences.pinnedRoutes || []);
      } else if (context?.previousLocalPins) {
        persistPinnedRoutesLocally(context.previousLocalPins);
      }
      console.error('Failed to toggle pin:', err);
    },
    // No invalidation needed - optimistic update is sufficient
    // The API call already succeeded and returned the same state we optimistically set
  });

  /**
   * Track recent route navigation
   * Auto-called when user navigates to a route
   * Uses optimistic update for instant UI feedback
   */
  const trackRecentRoute = useMutation({
    mutationFn: async (routeId) => {
      const response = await apiClient.post(
        `${config.REACT_APP_BACKEND_HOST}/configure/navigation/track-activity`,
        {
          routeId,
          timestamp: new Date().toISOString(),
        }
      );
      return response.data;
    },
    // Optimistic update - UI feels instant, syncs in background
    onMutate: async (routeId) => {
      // Cancel any outgoing refetches to prevent overwriting our optimistic update
      await queryClient.cancelQueries(userPreferencesQueryKey);

      // Snapshot the previous value
      const previousPreferences = queryClient.getQueryData(userPreferencesQueryKey);

      // Optimistically update to the new value
      queryClient.setQueryData(userPreferencesQueryKey, (old) => {
        if (!old) return old;

        const filtered = (old.recentRoutes || []).filter(r => r.routeId !== routeId);
        return {
          ...old,
          recentRoutes: [
            { routeId, timestamp: new Date().toISOString() },
            ...filtered
          ].slice(0, 10)
        };
      });

      return { previousPreferences };
    },
    // If mutation fails, roll back to previous value
    onError: (err, routeId, context) => {
      if (context?.previousPreferences) {
        queryClient.setQueryData(userPreferencesQueryKey, context.previousPreferences);
      }
      console.error('Failed to track recent route:', err);
    },
    // NO invalidation on settled - rely on optimistic update for instant UI
    // Removed to prevent infinite loop from query refetch cascade
    // onSettled: () => {
    //   queryClient.invalidateQueries(['user', currUser?.userId]);
    // },
  });

  /**
   * Update navigation preferences (collapsed categories, search history, etc.)
   */
  const updateNavigationPreferences = useMutation({
    mutationFn: async (preferences) => {
      const response = await apiClient.post(
        `${config.REACT_APP_BACKEND_HOST}/configure/navigation/user-preferences`,
        {
          navigationPreferences: {
            ...navigationPreferences,
            ...preferences,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(userPreferencesQueryKey);
    },
    onError: (error) => {
      console.error('Failed to update navigation preferences:', error);
    },
  });

  /**
   * Mark onboarding as complete
   */
  const completeOnboarding = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post(
        `${config.REACT_APP_BACKEND_HOST}/configure/navigation/user-preferences`,
        {
          navigationPreferences: {
            ...navigationPreferences,
            onboardingComplete: true,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(userPreferencesQueryKey);
    },
  });

  /**
   * Dismiss navigation update banner
   * Stores dismissal in user preferences so it syncs across devices
   */
  const dismissNavigationBanner = useMutation({
    mutationFn: async (bannerVersion = 'v1') => {
      const response = await apiClient.post(
        `${config.REACT_APP_BACKEND_HOST}/configure/navigation/user-preferences`,
        {
          navigationPreferences: {
            ...navigationPreferences,
            [`navigationBanner${bannerVersion}Dismissed`]: true,
            [`navigationBanner${bannerVersion}DismissedAt`]: new Date().toISOString(),
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(userPreferencesQueryKey);
    },
    onError: (error) => {
      console.error('Failed to dismiss navigation banner:', error);
    },
  });

  return {
    // Data from dedicated endpoint
    pinnedRoutes,
    recentRoutes,
    navigationPreferences,
    hasLoadedPreferences,

    // Loading state
    isLoading,

    // Route mutations
    pinRoute,
    unpinRoute,
    togglePin,
    trackRecentRoute,
    updateNavigationPreferences,
    completeOnboarding,
    dismissNavigationBanner,

    // Mutation states
    isPinning: pinRoute.isPending,
    isUnpinning: unpinRoute.isPending,
    isTogglingPin: togglePin.isPending,
    isTrackingRecent: trackRecentRoute.isPending,
    isUpdatingPreferences: updateNavigationPreferences.isPending,
    isDismissingBanner: dismissNavigationBanner.isPending,
  };
};

export default useUserRoutePreferences;
